import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { signIn, signUp, confirmSignUp, resetPassword } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';
import './AuthWrapper.css';

interface AuthWrapperProps {
  children?: React.ReactNode;
}

type AuthMode = 'signin' | 'signup' | 'confirm' | 'resetRequest' | 'resetConfirm';
type PasswordResetMode = 'request' | 'confirm';

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const { isAuthenticated, isLoading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const nextUrl = searchParams.get('next') || '/';
  // Raw invitation token from the invite link (?inviteToken=...). Passed through
  // signup so the post-confirm trigger can verify it against the stored hash.
  const inviteToken = searchParams.get('inviteToken') || '';
  
  const [authMode, setAuthMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tosChecked, setTosChecked] = useState(false);
  const [tosError, setTosError] = useState(false);
  
  // Password reset state
  const [passwordResetMode, setPasswordResetMode] = useState<PasswordResetMode>('request');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // Get contextual message based on next URL
  const getContextualMessage = (next: string): string | null => {
    if (!next || next === '/') return null;
    
    const messages: Record<string, string> = {
      '/graph': 'Sign in to view your Knowledge Graph',
      '/interventions': 'Sign in to view Interventions',
      '/settings': 'Sign in to access Settings',
      '/team': 'Sign in to manage your Team'
    };
    
    return messages[next] || 'Sign in to continue';
  };

  const contextualMessage = getContextualMessage(nextUrl);

  useEffect(() => {
    // Don't clear email when transitioning to password reset from sign-in
    if (authMode !== 'resetRequest' && authMode !== 'resetConfirm') {
      setEmail('');
    }
    setPassword('');
    setError('');
    setSuccessMessage('');
    setConfirmPassword('');
    setConfirmationCode('');
    setVerificationCode('');
    setNewPassword('');
    setConfirmNewPassword('');
    if (authMode !== 'resetConfirm') {
      setPasswordResetMode('request');
    }
  }, [authMode]);

  useEffect(() => {
    setEmail('');
    setPassword('');
    setError('');
    setSuccessMessage('');
    setConfirmPassword('');
    setConfirmationCode('');
    setVerificationCode('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordResetMode('request');
  }, []);

  // Redirect unauthenticated users accessing protected routes
  useEffect(() => {
    if (!isAuthenticated && children && !isLoading) {
      const currentPath = location.pathname;
      navigate(`/login?next=${encodeURIComponent(currentPath)}`, { replace: true });
    }
  }, [isAuthenticated, children, isLoading, location.pathname, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSubmitting(true);

    try {
      await signIn({ username: email, password });
      // Trigger AuthContext to re-check authentication
      await checkAuth();
      // Navigate to return URL after successful authentication
      navigate(decodeURIComponent(nextUrl));
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!tosChecked) {
      setTosError(true);
      return;
    }

    setTosError(false);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            ...(inviteToken ? { 'custom:inviteToken': inviteToken } : {}),
          },
        },
      });
      setAuthMode('confirm');
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to sign up');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSubmitting(true);

    try {
      await confirmSignUp({ username: email, confirmationCode });
      // Auto sign in after confirmation
      await signIn({ username: email, password });
      // Trigger AuthContext to re-check authentication
      await checkAuth();
      // Navigate to return URL after successful authentication
      navigate(decodeURIComponent(nextUrl));
    } catch (err: any) {
      setError(err.message || 'Failed to confirm sign up');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPasswordRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      await resetPassword({ username: email });
      setPasswordResetMode('confirm');
      setAuthMode('resetConfirm');
      setSuccessMessage('✓ Password reset code sent. Check your email.');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      const { confirmResetPassword } = await import('aws-amplify/auth');
      await confirmResetPassword({
        username: email,
        confirmationCode: verificationCode,
        newPassword: newPassword
      });
      setAuthMode('signin');
      setPasswordResetMode('request');
      setSuccessMessage('✓ Password reset successful. Please sign in.');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    // Transition to password reset request mode
    setAuthMode('resetRequest');
    setPasswordResetMode('request');
    setError('');
    setSuccessMessage('');
  };

  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
      </div>
    );
  }

  // Redirect unauthenticated users accessing protected routes
  if (!isAuthenticated && children) {
    return null; // Don't render anything, let useEffect handle navigation
  }

  if (isAuthenticated) {
    return <>{children ?? <Outlet />}</>;
  }

  return (
    <div style={styles.pageContainer}>
      {/* Animated background */}
      <div style={styles.backgroundOverlay} />
      
      <div style={styles.card}>
        {/* Logo and branding */}
        <div style={styles.header}>
          <div style={styles.logoContainer}>
            <div style={styles.logoGlow} />
            <svg 
              width="64" 
              height="64" 
              viewBox="0 0 40 40" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              style={styles.logo}
            >
              <circle cx="20" cy="8" r="3" fill="#60a5fa" />
              <circle cx="8" cy="20" r="3" fill="#60a5fa" />
              <circle cx="32" cy="20" r="3" fill="#60a5fa" />
              <circle cx="14" cy="32" r="3" fill="#60a5fa" />
              <circle cx="26" cy="32" r="3" fill="#60a5fa" />
              <line x1="20" y1="8" x2="8" y2="20" stroke="#60a5fa" strokeWidth="1.5" opacity="0.4" />
              <line x1="20" y1="8" x2="32" y2="20" stroke="#60a5fa" strokeWidth="1.5" opacity="0.4" />
              <line x1="8" y1="20" x2="14" y2="32" stroke="#60a5fa" strokeWidth="1.5" opacity="0.4" />
              <line x1="32" y1="20" x2="26" y2="32" stroke="#60a5fa" strokeWidth="1.5" opacity="0.4" />
              <line x1="14" y1="32" x2="26" y2="32" stroke="#60a5fa" strokeWidth="1.5" opacity="0.4" />
            </svg>
          </div>
          <h1 style={styles.title}>RetainIQ</h1>
          <p style={styles.subtitle}>Retain your team's critical knowledge</p>
          <p style={styles.tagline}>Predict & prevent knowledge loss</p>
          {contextualMessage && (
            <div style={styles.contextualMessage}>
              {contextualMessage}
            </div>
          )}
        </div>

        {/* Tabs */}
        {authMode !== 'confirm' && authMode !== 'resetRequest' && authMode !== 'resetConfirm' && (
          <div style={styles.tabs}>
            <button
              type="button"
              style={{
                ...styles.tab,
                ...(authMode === 'signin' ? styles.tabActive : {}),
              }}
              onClick={() => {
                setAuthMode('signin');
                setError('');
                setSuccessMessage('');
              }}
            >
              Sign In
            </button>
            <button
              type="button"
              style={{
                ...styles.tab,
                ...(authMode === 'signup' ? styles.tabActive : {}),
              }}
              onClick={() => {
                setAuthMode('signup');
                setError('');
                setSuccessMessage('');
              }}
            >
              Create Account
            </button>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div style={styles.success}>
            {successMessage}
          </div>
        )}

        {/* Sign In Form */}
        {authMode === 'signin' && (
          <form onSubmit={handleSignIn} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                style={styles.input}
                disabled={isSubmitting}
                autoComplete="off"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                style={styles.input}
                disabled={isSubmitting}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={handleForgotPassword}
                style={styles.forgotPasswordLink}
                disabled={isSubmitting}
              >
                Forgot Password?
              </button>
            </div>
            <button
              type="submit"
              style={{
                ...styles.button,
                ...(isSubmitting ? styles.buttonDisabled : {}),
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        {/* Sign Up Form */}
        {authMode === 'signup' && (
          <form onSubmit={handleSignUp} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                style={styles.input}
                disabled={isSubmitting}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password (min 8 characters)"
                required
                style={styles.input}
                disabled={isSubmitting}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                style={styles.input}
                disabled={isSubmitting}
              />
            </div>
            <div style={styles.tosContainer}>
              <label style={styles.tosLabel}>
                <input
                  type="checkbox"
                  checked={tosChecked}
                  onChange={(e) => {
                    setTosChecked(e.target.checked);
                    if (e.target.checked) setTosError(false);
                  }}
                  style={styles.tosCheckbox}
                  disabled={isSubmitting}
                />
                <span style={styles.tosText}>
                  I agree to the{' '}
                  <a href="/terms" style={styles.tosLink} target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a href="/privacy" style={styles.tosLink} target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                </span>
              </label>
              {tosError && (
                <p style={styles.tosError}>
                  You must agree to the Terms of Service and Privacy Policy to create an account.
                </p>
              )}
            </div>
            <button
              type="submit"
              style={{
                ...styles.button,
                ...(!tosChecked || isSubmitting ? styles.buttonDisabled : {}),
              }}
              disabled={!tosChecked || isSubmitting}
            >
              {isSubmitting ? 'Creating account...' : 'Create Account'}
            </button>
          </form>
        )}

        {/* Confirmation Form */}
        {authMode === 'confirm' && (
          <form onSubmit={handleConfirmSignUp} style={styles.form}>
            <p style={styles.confirmText}>
              We've sent a confirmation code to <strong>{email}</strong>. Please enter it below.
            </p>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="code">Confirmation Code</label>
              <input
                id="code"
                type="text"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                required
                maxLength={6}
                style={styles.input}
                disabled={isSubmitting}
              />
            </div>
            <button
              type="submit"
              style={{
                ...styles.button,
                ...(isSubmitting ? styles.buttonDisabled : {}),
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Verifying...' : 'Verify'}
            </button>
            <button
              type="button"
              style={styles.linkButton}
              onClick={() => setAuthMode('signup')}
            >
              Back to Sign Up
            </button>
          </form>
        )}

        {/* Password Reset Request Form */}
        {authMode === 'resetRequest' && (
          <form onSubmit={handleResetPasswordRequest} style={styles.form}>
            <p style={styles.confirmText}>
              Enter your email address and we'll send you a verification code to reset your password.
            </p>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                style={styles.input}
                disabled={isSubmitting}
              />
            </div>
            <button
              type="submit"
              style={{
                ...styles.button,
                ...(isSubmitting ? styles.buttonDisabled : {}),
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending code...' : 'Send Reset Code'}
            </button>
            <button
              type="button"
              style={styles.linkButton}
              onClick={() => {
                setAuthMode('signin');
                setPasswordResetMode('request');
              }}
            >
              Back to Sign In
            </button>
          </form>
        )}

        {/* Password Reset Confirmation Form */}
        {authMode === 'resetConfirm' && (
          <form onSubmit={handleConfirmResetPassword} style={styles.form}>
            <p style={styles.confirmText}>
              We've sent a verification code to <strong>{email}</strong>. Enter the code and your new password below.
            </p>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="verificationCode">Verification Code</label>
              <input
                id="verificationCode"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                required
                maxLength={6}
                style={styles.input}
                disabled={isSubmitting}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
                required
                style={styles.input}
                disabled={isSubmitting}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="confirmNewPassword">Confirm New Password</label>
              <input
                id="confirmNewPassword"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                style={styles.input}
                disabled={isSubmitting}
              />
            </div>
            <button
              type="submit"
              style={{
                ...styles.button,
                ...(isSubmitting ? styles.buttonDisabled : {}),
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Resetting password...' : 'Reset Password'}
            </button>
            <button
              type="button"
              style={styles.linkButton}
              onClick={() => {
                setAuthMode('signin');
                setPasswordResetMode('request');
              }}
            >
              Back to Sign In
            </button>
          </form>
        )}

        {/* Footer */}
        <div style={styles.footer}>
          © 2026 RetainIQ. Secure authentication powered by AWS Cognito.
        </div>
      </div>
    </div>
  );
};

// Inline styles - NO Amplify CSS, NO theme switching
const styles: { [key: string]: React.CSSProperties } = {
  pageContainer: {
    minHeight: '100vh',
    background: '#0f172a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    position: 'relative',
    overflow: 'hidden',
    transition: 'none',
  },
  backgroundOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.03) 0%, transparent 50%)
    `,
    pointerEvents: 'none',
    transition: 'none',
  },
  loadingContainer: {
    minHeight: '100vh',
    background: '#0f172a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'none',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #334155',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  card: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '8px',
    padding: '48px',
    width: '520px',
    maxWidth: '90vw',
    margin: '0 auto',
    position: 'relative',
    zIndex: 1,
    transition: 'none',
  },
  header: {
    textAlign: 'center' as const,
    marginBottom: '2rem',
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1.5rem',
    position: 'relative' as const,
  },
  logoGlow: {
    position: 'absolute' as const,
    width: '80px',
    height: '80px',
    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
    filter: 'blur(20px)',
    zIndex: 0,
  },
  logo: {
    position: 'relative' as const,
    zIndex: 1,
    filter: 'drop-shadow(0 8px 16px rgba(59, 130, 246, 0.4))',
  },
  title: {
    fontSize: '2.25rem',
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: '0.5rem',
    letterSpacing: '-0.025em',
    margin: '0 0 0.5rem 0',
  },
  subtitle: {
    fontSize: '1rem',
    color: '#94a3b8',
    fontWeight: 500,
    letterSpacing: '0.025em',
    marginBottom: '0.5rem',
    margin: '0 0 0.5rem 0',
  },
  tagline: {
    fontSize: '0.875rem',
    color: '#64748b',
    fontWeight: 400,
    fontStyle: 'italic' as const,
    margin: 0,
  },
  contextualMessage: {
    marginTop: '1rem',
    padding: '0.75rem 1rem',
    background: 'rgba(59, 130, 246, 0.1)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '8px',
    color: '#93c5fd',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #334155',
    marginBottom: '1.5rem',
    gap: 0,
  },
  tab: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    padding: '0.75rem 1.5rem',
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: '#64748b',
    cursor: 'pointer',
    transition: 'color 0.2s, border-color 0.2s',
  },
  tabActive: {
    color: '#ffffff',
    borderBottomColor: '#3b82f6',
  },
  form: {
    marginTop: '1.5rem',
  },
  formGroup: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '0.5rem',
  },
  input: {
    width: '100%',
    height: '48px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    fontSize: '0.9375rem',
    color: '#ffffff',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.2s',
  },
  button: {
    width: '100%',
    height: '48px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.2s, transform 0.2s',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  linkButton: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    color: '#3b82f6',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: '1rem',
    padding: '0.5rem',
  },
  error: {
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    color: '#fca5a5',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  success: {
    background: 'rgba(34, 197, 94, 0.1)',
    border: '1px solid rgba(34, 197, 94, 0.3)',
    color: '#86efac',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  confirmText: {
    color: '#cbd5e1',
    fontSize: '0.9375rem',
    marginBottom: '1.5rem',
    lineHeight: 1.6,
  },
  footer: {
    textAlign: 'center' as const,
    marginTop: '2rem',
    paddingTop: '2rem',
    borderTop: '1px solid #334155',
    fontSize: '0.75rem',
    color: '#475569',
  },
  forgotPasswordLink: {
    background: 'transparent',
    border: 'none',
    color: '#3b82f6',
    fontSize: '0.875rem',
    cursor: 'pointer',
    marginTop: '0.5rem',
    padding: 0,
    textAlign: 'left' as const,
    fontWeight: 500,
    transition: 'color 0.2s',
  },
  tosContainer: {
    marginBottom: '1.25rem',
  },
  tosLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.625rem',
    cursor: 'pointer',
  },
  tosCheckbox: {
    marginTop: '2px',
    width: '16px',
    height: '16px',
    flexShrink: 0,
    accentColor: '#3b82f6',
    cursor: 'pointer',
  },
  tosText: {
    fontSize: '0.875rem',
    color: '#94a3b8',
    lineHeight: 1.5,
  },
  tosLink: {
    color: '#60a5fa',
    textDecoration: 'underline',
  },
  tosError: {
    marginTop: '0.5rem',
    fontSize: '0.8125rem',
    color: '#fca5a5',
    margin: '0.5rem 0 0 0',
  },
};

export default AuthWrapper;
